import { useCallback, useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import type { Deal } from '@/types'
import { useFocusTrap } from '@/hooks/useFocusTrap'
import { DealInfoTab } from './DealDetailPanel/DealInfoTab'
import { isRocketInstance } from '@/config/instanceProfile'
import { DealProposalTab } from './DealDetailPanel/DealProposalTab'
import { DealHistoryTab } from './DealDetailPanel/DealHistoryTab'
import { DealContractTab } from './DealDetailPanel/DealContractTab'
import { FooterActions } from './DealDetailPanel/FooterActions'
import { DealDetailHeader } from './DealDetailPanel/DealDetailHeader'
import { DealDetailPanelTabs } from './DealDetailPanel/DealDetailPanelTabs'
import { DealDetailPanelModals } from './DealDetailPanel/DealDetailPanelModals'
import { DEAL_DETAIL_PANEL_MAX_WIDTH } from './DealDetailPanel/dealDetailPanelConfig'
import {
  useDealDetailPanel,
  type DealProposalGuardState,
  type DealProposalPanelActions,
} from './DealDetailPanel/useDealDetailPanel'
import { isProposalTabVisible } from './deals-constants'
import { useDealsStore } from '@/stores/useDealsStore'

export { getDefaultPanelTab } from './DealDetailPanel/dealDetailPanelConfig'

interface DealDetailPanelProps {
  deal: Deal | null
  onClose: () => void
  onDealUpdated?: (deal: Deal) => void
  onDealDeleted?: (dealId: string) => void
}

export function DealDetailPanel({
  deal,
  onClose,
  onDealUpdated,
  onDealDeleted,
}: DealDetailPanelProps) {
  const asideRef = useRef<HTMLElement>(null)
  const setDealDetailUiOpen = useDealsStore((s) => s.setDealDetailUiOpen)
  const [proposalGuard, setProposalGuard] = useState<DealProposalGuardState>({
    proposals: [],
    draftItens: [],
  })
  const [proposalActions, setProposalActions] = useState<DealProposalPanelActions | null>(null)

  useEffect(() => {
    setProposalGuard({ proposals: [], draftItens: [] })
    setProposalActions(null)
  }, [deal?.id])

  const {
    scrollRef,
    currentDeal,
    activeTab,
    setActiveTab,
    showDeleteModal,
    setShowDeleteModal,
    showLossModal,
    setShowLossModal,
    showFutureModal,
    setShowFutureModal,
    showWinModal,
    setShowWinModal,
    showStageChoice,
    setShowStageChoice,
    showContractDataModal,
    setShowContractDataModal,
    contractDataModalMode,
    contractDataDeal,
    blockedRequirements,
    setBlockedRequirements,
    clearDealPanelHighlight,
    handleDealPatch,
    registerReuniaoConfirm,
    handleFooterPrimary,
    handleReopenRenegotiation,
    handleStageChoice,
    handleContractDataConfirm,
    footerPrimary,
    footerPrimaryDisabled,
    footerPrimaryLoading,
    canDelete,
    canScheduleFuture,
    isClosed,
    panelHighlightFields,
  } = useDealDetailPanel(deal, onDealUpdated, proposalGuard, onClose, proposalActions)

  useEffect(() => {
    if (!currentDeal) return
    setDealDetailUiOpen(true)
    return () => setDealDetailUiOpen(false)
  }, [currentDeal?.id, setDealDetailUiOpen])

  useFocusTrap(asideRef, Boolean(currentDeal))

  const handleClosePanel = useCallback(() => {
    if (currentDeal) clearDealPanelHighlight(currentDeal.id)
    onClose()
  }, [currentDeal, clearDealPanelHighlight, onClose])

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') handleClosePanel()
    }
    if (currentDeal) document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [currentDeal, handleClosePanel])

  if (!currentDeal) return null

  return (
    <AnimatePresence>
      <motion.div
        key="deal-detail-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-40 bg-black/30 md:backdrop-blur-[2px]"
        onClick={handleClosePanel}
        aria-hidden
      />

      <motion.aside
        ref={asideRef}
        key="deal-detail-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="deal-detail-title"
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 32, stiffness: 320 }}
        className={`fixed right-0 top-0 z-50 flex h-full w-full ${DEAL_DETAIL_PANEL_MAX_WIDTH} flex-col border-l border-white/[0.08] bg-bg-main shadow-2xl`}
      >
        <span id="deal-detail-title" className="sr-only">
          Detalhes do deal {currentDeal.empresaNome}
        </span>

        <DealDetailHeader deal={currentDeal} onClose={handleClosePanel} />

        <DealDetailPanelTabs
          deal={currentDeal}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 md:p-5">
          {activeTab === 'info' && (
            <DealInfoTab
              deal={currentDeal}
              onDealPatched={handleDealPatch}
              highlightFields={panelHighlightFields}
              onRegisterReuniaoConfirm={registerReuniaoConfirm}
            />
          )}
          {isProposalTabVisible(currentDeal.etapa) && (
            <div className={activeTab === 'proposal' ? undefined : 'hidden'} aria-hidden={activeTab !== 'proposal'}>
              <DealProposalTab
                deal={currentDeal}
                catalogScope={isRocketInstance ? 'rocket' : 'deals'}
                onDealPatched={handleDealPatch}
                onProposalStateChange={setProposalGuard}
                onRegisterProposalActions={setProposalActions}
              />
            </div>
          )}
          {activeTab === 'contract' && <DealContractTab deal={currentDeal} />}
          {activeTab === 'history' && (
            <DealHistoryTab
              dealId={currentDeal.id}
              dealEtapa={currentDeal.etapa}
              onReopenRenegotiation={
                currentDeal.etapa === 'perdido' ? handleReopenRenegotiation : undefined
              }
            />
          )}
        </div>

        <FooterActions
          canDelete={canDelete}
          isClosed={isClosed}
          canScheduleFuture={canScheduleFuture}
          primaryLabel={footerPrimary?.label}
          primaryIcon={footerPrimary?.icon}
          primaryClassName={footerPrimary?.className}
          primaryDisabled={footerPrimaryDisabled}
          primaryLoading={footerPrimaryLoading}
          onDelete={() => setShowDeleteModal(true)}
          onLoss={() => setShowLossModal(true)}
          onFuture={() => setShowFutureModal(true)}
          onPrimary={footerPrimary ? handleFooterPrimary : undefined}
        />

        <DealDetailPanelModals
          deal={currentDeal}
          showDelete={showDeleteModal}
          showLoss={showLossModal}
          showFuture={showFutureModal}
          showWin={showWinModal}
          showStageChoice={showStageChoice}
          showContractData={showContractDataModal}
          contractDataModalMode={contractDataModalMode}
          contractDataDeal={contractDataDeal ?? currentDeal}
          blockedRequirements={blockedRequirements}
          onCloseDelete={() => setShowDeleteModal(false)}
          onCloseLoss={() => setShowLossModal(false)}
          onCloseFuture={() => setShowFutureModal(false)}
          onCloseWin={() => setShowWinModal(false)}
          onCloseStageChoice={() => setShowStageChoice(false)}
          onCloseContractData={() => setShowContractDataModal(false)}
          onContractDataConfirm={handleContractDataConfirm}
          onClearBlocked={() => setBlockedRequirements([])}
          onGoToInfo={() => {
            setBlockedRequirements([])
            setActiveTab('info')
          }}
          onDealDeleted={onDealDeleted}
          onDealPatched={handleDealPatch}
          onStageChoice={handleStageChoice}
        />
      </motion.aside>
    </AnimatePresence>
  )
}
